import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { otcAPI } from '../api/otc';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C, ThemedInput } from '../components/ui/ThemedSurface';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { QueryErrorNote } from '../components/ui/QueryErrorNote';
import { formatApiErrorMessage } from '../utils/api-error-message';
import { formatHangDollars } from '../components/wallet/HangTradePanel';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

export function BuyQueuePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = searchInput.trim();
      setSearch((prev) => {
        if (prev !== next) setPage(1);
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const { data, isPending, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['otc', 'buy-queue', page, search],
    queryFn: () => otcAPI.getBuyQueue({ page, limit: PAGE_SIZE, search: search || undefined }),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });

  const showSkeleton = isPending && !data;

  const rows = data?.rows ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  const serialOffset = useMemo(() => (page - 1) * PAGE_SIZE, [page]);

  function clearSearch() {
    setSearchInput('');
    setSearch('');
    setPage(1);
  }

  return (
    <div className="relative min-h-full" style={{ background: '#131f2f' }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('buyQueue.title')} onBack={() => navigate(-1)} theme={C} />

        <div className="px-4 pb-28 pt-[70px]">
          <div
            className="mb-3 rounded-lg border px-3 py-2.5"
            style={{ borderColor: '#2a3648', background: '#222d3f' }}
          >
            <label htmlFor="buy-queue-search" className="mb-1.5 block text-[11px] font-semibold text-white/70">
              {t('buyQueue.searchLabel')}
            </label>
            <div className="flex items-center gap-2">
              <ThemedInput
                id="buy-queue-search"
                type="search"
                inputMode="numeric"
                autoComplete="off"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('buyQueue.searchPlaceholder')}
                className="flex-1 text-sm text-white"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const next = searchInput.trim();
                    setSearch(next);
                    setPage(1);
                  }
                }}
              />
              {searchInput ? (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="shrink-0 rounded-lg px-3 py-2 text-xs font-semibold text-white/70"
                  style={{ background: '#131f2f' }}
                >
                  {t('buyQueue.clear')}
                </button>
              ) : null}
            </div>
            {search ? (
              <p className="mt-2 text-[10px] text-white/50">
                {t('buyQueue.searchActive', { id: search })}
              </p>
            ) : null}
          </div>

          {isError && (
            <QueryErrorNote
              message={formatApiErrorMessage(error, t('buyQueue.loadFailed'))}
              onRetry={() => void refetch()}
            />
          )}

          <div className="overflow-hidden rounded-lg border" style={{ borderColor: '#2a3648' }}>
            <div
              className="grid grid-cols-[44px_1fr_72px_64px_1.2fr] gap-1 px-2 py-2 text-[10px] font-semibold text-white/80"
              style={{ background: '#222d3f' }}
            >
              <span className="text-center">{t('buyQueue.colNo')}</span>
              <span className="text-center">{t('buyQueue.colBuyerId')}</span>
              <span className="text-center">{t('buyQueue.colFunds')}</span>
              <span className="text-center">{t('buyQueue.colStatus')}</span>
              <span className="text-center">{t('buyQueue.colTime')}</span>
            </div>

            {showSkeleton ? (
              <div className="space-y-2 p-3">
                <LoadingSkeleton className="h-10" />
                <LoadingSkeleton className="h-10" />
                <LoadingSkeleton className="h-10" />
              </div>
            ) : rows.length === 0 ? (
              <div className="px-4 py-10 text-center text-xs text-white/50">
                {search ? t('buyQueue.noMatch', { id: search }) : t('common.noData')}
              </div>
            ) : (
              <div className={isFetching ? 'opacity-60' : undefined}>
                {rows.map((row, idx) => (
                  <div
                    key={`${row.buyer_id}-${row.queue_time}-${idx}`}
                    className="grid grid-cols-[44px_1fr_72px_64px_1.2fr] gap-1 px-2 py-2.5 text-[11px] text-white"
                    style={{ background: idx % 2 === 1 ? '#131f2f' : '#222d3f' }}
                  >
                    <span className="text-center">{serialOffset + idx + 1}</span>
                    <span className="truncate text-center">{row.buyer_id}</span>
                    <span className="text-center" style={{ color: C.gold }}>
                      ${formatHangDollars(row.queue_funds)}
                    </span>
                    <span className="text-center">{row.status_zh}</span>
                    <span className="truncate text-center text-[10px]">{row.queue_time}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="mt-3 flex items-center justify-center gap-3 text-xs text-white/70">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded px-2 py-1 disabled:opacity-40"
                style={{ background: '#222d3f' }}
              >
                ‹
              </button>
              <span>
                {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="rounded px-2 py-1 disabled:opacity-40"
                style={{ background: '#222d3f' }}
              >
                ›
              </button>
            </div>
          )}
        </div>

        <div
          className="fixed inset-x-0 bottom-0 z-20 mx-auto flex max-w-[430px] items-stretch justify-between border-t px-8 py-4"
          style={{ borderColor: '#2a3648', background: '#131f2f' }}
        >
          <div className="flex flex-1 flex-col items-center gap-1">
            <span className="text-lg font-semibold text-white">{data?.new_number ?? '—'}</span>
            <span className="text-[11px] text-white/60">{t('buyQueue.latestId')}</span>
          </div>
          <div className="flex flex-1 flex-col items-center gap-1">
            <span className="text-lg font-semibold text-white">{data?.total ?? 0}</span>
            <span className="text-[11px] text-white/60">{t('buyQueue.totalInQueue')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BuyQueuePage;
